import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"

import { PERCENT, DECIMAL } from "const"
import { StoreType, UserSettings, LangType } from "client/store"
import { SilPercentageFormat } from "client/utils"

import FormatInput from "./input"
import FormatView from "./view"


const format = (
    data: string | number | false,
    percentage: SilPercentageFormat,
    percentageDecimal: number,
) => {
    const value = typeof data === "string" ? parseFloat(data) : data
    if (value === undefined) {
        return null
    }
    if (value === null) { // not used
        return null
    }
    if (value === false || Number.isNaN(value) || value < 0) { // incorrect value
        return false
    }

    if (percentage === DECIMAL) {
        return value.toFixed(percentageDecimal)
    }
    if (percentage === PERCENT) {
        return (value * 100).toFixed(percentageDecimal)
    }

    return null
}


export const FormatPercentageString = (
    data: string | number | false,
    userSettings: UserSettings,
    lang: LangType,
) => {
    const { percentage, percentageDecimal } = userSettings.sil
    const result = format(data, percentage, percentageDecimal)

    if (result === null) return lang.notUsed
    if (result === false) return lang.insufficientData

    if (percentage === PERCENT) {
        return `${result} %`
    }

    return result
}


interface FormatProps {
    title?: string
    data: string | number | false
    tableView?: boolean
    inputView?: boolean
    validation?: string
    onChange?: (value: string) => void
}

const FormatPercentage = ({
    title = null,
    data,
    tableView = false,
    inputView = false,
    validation = null,
    onChange = null,
}: FormatProps) => {
    const silSettings = useSelector((state: StoreType) => state.system.userSettings.sil)
    const { percentage, percentageDecimal } = silSettings

    const [formatted, setFormatted] = useState<string | false>("")

    useEffect(() => {
        setFormatted(format(data, percentage, percentageDecimal))
    }, [data, percentage, percentageDecimal])

    const onChangeFormatted: React.ChangeEventHandler<HTMLInputElement> = (e) => setFormatted(e.target.value)
    const onChangeNull = () => {
        onChange(null)
        setFormatted(null)
    }
    const onChangeResult = (value: number) => {
        onChange(value.toString())
        setFormatted(format(value, percentage, percentageDecimal))
    }


    if (percentage === DECIMAL) {
        if (!onChange) {
            if (inputView) return <FormatInput title={title} value={formatted || ""} readOnly />
            return (
                <FormatView
                    title={title}
                    value={formatted}
                    tableView={tableView}
                />
            )
        }
        return (
            <FormatInput
                title={title}
                value={formatted || ""}
                validation={validation}
                onChange={onChangeFormatted}
                onBlur={(e) => {
                    const parsedValue = parseFloat(e.target.value)
                    if (Number.isNaN(parsedValue)) onChangeNull()
                    else if (parsedValue >= 0 && parsedValue <= 1) onChangeResult(parsedValue)
                    else onChangeNull()
                }}
            />
        )
    }

    if (percentage === PERCENT) {
        if (!onChange) {
            if (inputView) return <FormatInput title={title} value={formatted || ""} after="%" readOnly />
            return (
                <FormatView
                    title={title}
                    value={formatted}
                    tableView={tableView}
                    after="%"
                />
            )
        }
        return (
            <FormatInput
                title={title}
                value={formatted || ""}
                after="%"
                validation={validation}
                onChange={onChangeFormatted}
                onBlur={(e) => {
                    const parsedValue = parseFloat(e.target.value)
                    if (Number.isNaN(parsedValue)) onChangeNull()
                    else if (parsedValue >= 0 && parsedValue <= 100) onChangeResult(parsedValue / 100)
                    else onChangeNull()
                }}
            />
        )
    }

    return null
}

export default FormatPercentage
